import React, {
	PropsWithChildren,
	useCallback,
	useEffect,
	useRef,
	useState,
} from 'react';
import { User, UsersEntity } from '@citrite/sf-api';
import {
	FetchResult,
	HeadlessService,
	IMessage,
	IPaginatedResponse,
} from '@novu/headless';
import { notification } from 'antd';
import memoizePromiseFn from 'memoize-promise-fn';
import { NotifyHttp } from './NotifyHttp';
import { t } from './translate';
import { NovuConfiguration } from './types';
import { ActionTypes, useNotificationStore } from './useNotificationStore';
import { useUserContext } from './UserContext';

interface NotificationContextParams {
	deleteAllNotifications: () => void;
	deleteNotification: (messageId: string) => void;
	fetchNotifications: () => void;
	fetchStatus: string;
	getCachedUser: (arg0: string) => Promise<User>;
	hasErrors: boolean;
	hasMore: boolean;
	mainDrawerOpen: boolean;
	markAllMessagesAsRead: () => void;
	markNotificationsAsRead: (messageIds: string[]) => void;
	markNotificationsAsSeen: (messageIds: string[]) => void;
	notifications: IMessage[];
	pageNum: number;
	setMainDrawerOpen: (state: boolean) => void;
	setPageNum: React.Dispatch<React.SetStateAction<number>>;
	setSettingsDrawerOpen: (state: boolean) => void;
	settingsDrawerOpen: boolean;
	unreadCount: number;
}

const NotificationContext = React.createContext<NotificationContextParams>(
	{} as NotificationContextParams
);
const getCachedUser = memoizePromiseFn(arg => UsersEntity.get(arg[0]).execute());

export const NotificationProvider: React.FC<PropsWithChildren> = ({ children }) => {
	const [state, dispatch] = useNotificationStore();
	const { notifications, hasMore } = state;
	const [fetchStatus, setfetchStatus] = useState<string>(undefined);
	const { user } = useUserContext();
	const headlessServiceRef = useRef<HeadlessService>(null);
	const [pageNum, setPageNum] = useState<number>(0);
	const [novuConfiguration, setNovuConfiguration] = useState<NovuConfiguration>();
	const [unreadCount, setUnreadCount] = useState(0);
	const [hasErrors, setHasErrors] = useState<boolean>(false);
	const [mainDrawerOpen, setMainDrawerOpen] = useState(false);
	const [settingsDrawerOpen, setSettingsDrawerOpen] = useState(false);

	const fetchNotifications = useCallback(() => {
		const headlessService = headlessServiceRef.current;
		if (headlessService) {
			const unsubscribe = headlessService.fetchNotifications({
				listener: (result: FetchResult<IPaginatedResponse<IMessage>>) => {
					setfetchStatus(result.status);
				},
				onSuccess: (response: IPaginatedResponse<IMessage>) => {
					dispatch({
						type: ActionTypes.AddNewNotifications,
						data: {
							incomingNotifications: response.data,
							incomingHasMore: response.hasMore,
							currentPageNum: pageNum,
						},
					}); // Store notifications in the state
					setfetchStatus('success');
					unsubscribe();
				},
				page: pageNum,
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pageNum]);

	const fetchUnreadCount = useCallback(() => {
		const headlessService = headlessServiceRef.current;
		if (headlessService) {
			headlessService.fetchUnreadCount({
				listener: () => {},
				onSuccess: (data: { count: number }) => {
					setUnreadCount(data.count);
				},
				onError: () => {
					//TODO: add logger
				},
			});
		}
	}, []);

	useEffect(() => {
		const getNovuConfiguration = async () => {
			NotifyHttp.getNovuConfiguration().then(result => {
				setNovuConfiguration(result);
			});
		};

		getNovuConfiguration();
	}, []);

	useEffect(() => {
		if (novuConfiguration) {
			const headlessService = new HeadlessService({
				applicationIdentifier: novuConfiguration.appIdentifier,
				subscriberId: user.Id,
				subscriberHash: novuConfiguration.hmac,
				backendUrl: novuConfiguration.serviceUrls.apiUrl,
				socketUrl: novuConfiguration.serviceUrls.webSocketUrl,
			});

			return headlessService.initializeSession({
				listener: () => {
					// Function needs to exist but we have no use case for it yet
				},
				onSuccess: () => {
					headlessServiceRef.current = headlessService;
					fetchNotifications();
					fetchUnreadCount();
				},
				onError: () => {
					//handle initialization errors
					setHasErrors(true);
				},
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [fetchNotifications, novuConfiguration]);

	useEffect(() => {
		const headlessService = headlessServiceRef.current;
		if (headlessService) {
			return headlessService.listenUnreadCountChange({
				listener: (count: number) => {
					if (pageNum !== 0) {
						setPageNum(0);
					} else {
						fetchNotifications();
					}
					setUnreadCount(count);
				},
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [headlessServiceRef.current]);

	// Function to mark notifications as read
	const markNotificationsAsRead = (messageIds: string[]) => {
		if (!Array.isArray(messageIds)) {
			messageIds = [messageIds];
		}

		const headlessService = headlessServiceRef.current;

		if (headlessService) {
			headlessService.markNotificationsAsRead({
				messageId: messageIds,
				listener: () => {
					// Function needs to exist but we have no use case for it yet
				},
				onSuccess: () => {
					dispatch({ type: ActionTypes.MarkAsRead, data: { messageIds } });
				},
				onError: () => {
					//error handling for marking as read
					notification.error({
						message: t('notification_center:error.card_title'),
						description: t('notification_center:error.mark_as_read_error'),
					});
				},
			});
		}
	};

	const markNotificationsAsSeen = (messageIds: string[]) => {
		const headlessService = headlessServiceRef.current;
		if (headlessService) {
			headlessService.markNotificationsAsSeen({
				messageId: messageIds,
				listener: () => {},
				onSuccess: () => {},
				onError: () => {
					//error handling for marking as seen
				},
			});
		}
	};

	//maybe unneeded
	const deleteNotification = (messageId: string) => {
		const headlessService = headlessServiceRef.current;
		if (headlessService) {
			headlessService.removeNotification({
				messageId,
				listener: () => {},
				onSuccess: () => {},
				onError: () => {
					//error handling for deleting notif
				},
			});
		}
	};

	const deleteAllNotifications = () => {
		const headlessService = headlessServiceRef.current;
		if (headlessService) {
			headlessService.removeAllNotifications({
				listener: () => {},
				onSuccess: () => {
					dispatch({ type: ActionTypes.DeleteAllNotifications });
				},
				onError: () => {
					notification.error({
						message: t('notification_center:error.card_title'),
						description: t('notification_center:error.clear_all_error'),
					});
				},
			});
		}
	};

	const markAllMessagesAsRead = () => {
		const headlessService = headlessServiceRef.current;

		headlessService.markAllMessagesAsRead({
			listener: () => {
				// function needs to exist but we have no use case for it yet
				// Handle the result of marking all messages as read
			},
			onSuccess: () => {
				dispatch({ type: ActionTypes.MarkAllAsRead });
			},
			onError: () => {
				//error handling for marking all as read
				notification.error({
					message: t('notification_center:error.card_title'),
					description: t('notification_center:error.mark_all_as_read_error'),
				});
			},
		});
	};

	return (
		<NotificationContext.Provider
			value={{
				deleteAllNotifications,
				deleteNotification,
				fetchNotifications,
				fetchStatus,
				getCachedUser,
				hasErrors,
				hasMore,
				mainDrawerOpen,
				markAllMessagesAsRead,
				markNotificationsAsRead,
				markNotificationsAsSeen,
				notifications,
				pageNum,
				setMainDrawerOpen,
				setPageNum,
				setSettingsDrawerOpen,
				settingsDrawerOpen,
				unreadCount,
			}}
		>
			{children}
		</NotificationContext.Provider>
	);
};

export const useNotificationContext = () => React.useContext(NotificationContext);
